import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetProgramsQuery } from '../../../redux/api/manufacturing';
import EmbedHeader from './EmbedHeader';
import EmbedProgramIssueLogPage from './EmbedProgramIssueLogPage';
import {
	embedProgramLookupParams,
	formatEmbedOrderPartLabel,
	readEmbedOrderPartContext,
} from './embedLookup';

import './embed.css';

const Shell: React.FC<{
	title: string;
	tone: 'loading' | 'error';
	children: React.ReactNode;
}> = ({ title, tone, children }) => (
	<div className="embed-program-review">
		<EmbedHeader title={title} />
		<div className={`embed-program-review__${tone}`}>{children}</div>
	</div>
);

/**
 * Resolves a CAM program from the FASTLANE folder layout reported by the
 * Fusion add-in: FASTLANE / <Order> / <Part> / <file>.
 */
const EmbedProgramByFilename: React.FC = () => {
	const [searchParams] = useSearchParams();
	const context = readEmbedOrderPartContext(searchParams);
	const label = context ? formatEmbedOrderPartLabel(context) : 'Program';

	const { data, isLoading, isError } = useGetProgramsQuery(
		embedProgramLookupParams(context!),
		{ skip: !context }
	);

	if (!context) {
		return (
			<Shell title="Program" tone="error">
				No order and part were provided.
			</Shell>
		);
	}

	if (isLoading) {
		return (
			<Shell title={label} tone="loading">
				Loading program…
			</Shell>
		);
	}

	if (isError) {
		return (
			<Shell title={label} tone="error">
				Unable to look up {label}.
			</Shell>
		);
	}

	const program = data?.results?.[0];

	if (!program) {
		return (
			<Shell title={label} tone="error">
				No CAM program was found for order {context.orderName}, part{' '}
				{context.partName}.
			</Shell>
		);
	}

	return <EmbedProgramIssueLogPage programId={program.id} />;
};

export default EmbedProgramByFilename;
